import { getDB } from '../utils/crypto';
import { validateQRToken } from '../utils/crypto';
import { addToSyncQueue, processSyncQueue } from '../utils/sync';

// Interface for QR scan data
interface QRScan {
  id?: number;
  token: string;
  qrNumber: string;
  client_timestamp: string;
  synced?: boolean;
}

// Environment variable for public key (would be stored in .env file)
const PUBLIC_KEY = import.meta.env.VITE_ADMIN_PUBLIC_KEY;

// Process a scanned QR code
export const processQRCode = async (qrContent: string): Promise<{ success: boolean, qrNumber: string | null, emoji?: string }> => {
  try {
    // QR codes should contain JWT after # in URL
    const urlParts = qrContent.split('#');
    
    if (urlParts.length !== 2) {
      console.error('Invalid QR code format');
      return { success: false, qrNumber: null };
    }
    
    const token = urlParts[1];
    const publicKeyObj = JSON.parse(PUBLIC_KEY); // Assuming the public key is stored in JWK format
    
    // Validate the JWT token (now returns object with qrNumber and emoji)
    const tokenData = await validateQRToken(token, publicKeyObj);
    
    if (!tokenData || !tokenData.qrNumber) {
      console.error('Invalid QR token');
      return { success: false, qrNumber: null };
    }
    
    const { qrNumber, emoji } = tokenData;
    
    // Store the scanned QR in IndexedDB
    const db = await getDB();
    const timestamp = new Date().toISOString();
    
    // Get user info from IndexedDB
    const userInfo = await db.get('user_data', 'user_info');
    const userId = userInfo?.user_id;
    
    if (!userId) {
      console.error('User ID not found');
      return { success: false, qrNumber: null };
    }
    
    // Store locally with original field names for compatibility
    await db.add('qr_scans', {
      token,
      qrNumber,
      client_timestamp: timestamp,
      synced: false
    });
    
    // Add to sync queue with database schema field names
    await addToSyncQueue('qr_scans', 'INSERT', {
      user_id: userId,
      token,
      qr_number: parseInt(qrNumber),
      scanned_at_client: timestamp
    });
    
    // If online, immediately process the sync queue
    if (navigator.onLine) {
      processSyncQueue().catch(console.error);
    }
    
    return { success: true, qrNumber, emoji };
  } catch (error) {
    console.error('Error processing QR code:', error);
    return { success: false, qrNumber: null };
  }
};

// Get all scanned QR codes
export const getScannedQRCodes = async (): Promise<QRScan[]> => {
  try {
    const db = await getDB();
    return await db.getAll('qr_scans');
  } catch (error) {
    console.error('Error getting scanned QR codes:', error);
    return [];
  }
};